import { TmBaseComponent } from '@treasury/presentation';
import { DialogOpenedChangedEvent } from '@vaadin/dialog';
import { dialogFooterRenderer, dialogRenderer } from '@vaadin/dialog/lit.js';
import { CSSResult, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PhoneNumber } from '../data/phone-number';
import type { LoginClient } from '../login-client';
import type { VerificationMethod } from '../types';
import { enterState } from './oob-registration-dialog-states/enter';
import { selectPreferencesState } from './oob-registration-dialog-states/select-preference';
import { verifyState } from './oob-registration-dialog-states/verify';

// eslint-disable-next-line no-duplicate-imports
import '@vaadin/dialog';

export interface PhoneNumberData {
    value: string;
    formatted: string;
}

export const tagName = 'oob-registration-dialog';
@customElement(tagName)
export class OobRegistrationDialog extends TmBaseComponent {
    @property({ type: Boolean })
    public useForBoth = false;

    @property({ type: Boolean })
    public dialogOpened = false;

    @property({ type: String })
    public oneTimePassword = '';

    @property({ type: Object })
    public state = enterState;

    @property({ type: Boolean })
    public isLoading = false;

    @property({ type: Object })
    public client?: LoginClient;

    @property({ type: Object })
    public verificationMethod?: VerificationMethod;

    @property({ type: String })
    public errorMessage?: string;

    @property({
        attribute: false,
    })
    public phoneNumber?: PhoneNumberData;

    @property({ type: String })
    public preferredMethod = '';

    public get title() {
        return this.state.title;
    }

    public get isOneTimePasswordValid() {
        if (!this.verificationMethod) {
            return false;
        }

        return this.oneTimePassword.length === this.verificationMethod.oneTimePasswordLength;
    }

    public async verify() {
        if (!this.verificationMethod) {
            throw new Error('Cannot verify. No verification method provided.');
        }

        if (!this.client) {
            throw new Error('Cannot verify. No client provided.');
        }

        try {
            this.isLoading = true;
            const response = await this.client.validateOneTimePassword(this.oneTimePassword);

            if (response.status === 'Success') {
                if (!this.phoneNumber) {
                    throw new Error('Could not verify. No valid phone number.');
                }

                this.verificationMethod.isVerified = true;
                this.preferredMethod = this.verificationMethod.id;
                this.verificationMethod.phoneNumber = new PhoneNumber(this.phoneNumber.value);
                if (this.useForBoth) {
                    this.verificationMethod.other.isVerified = true;
                    this.verificationMethod.other.phoneNumber = new PhoneNumber(
                        this.phoneNumber.value
                    );
                }
                this.errorMessage = undefined;
                this.state = selectPreferencesState;
            } else {
                this.errorMessage =
                    'message' in response && typeof response.message === 'string'
                        ? response.message
                        : // cspell:ignore passcode
                          'Invalid passcode';
                this.state = enterState;
            }
        } catch (e) {
            this.state = enterState;
            this.errorMessage = e instanceof Error ? e.message : 'Invalid passcode';
        } finally {
            this.isLoading = false;
        }
    }

    public async finish() {
        if (!this.client) {
            throw new Error('Could not finish registration. No client provided.');
        }

        this.isLoading = true;
        await this.client.setOutOfBandPreferredMethod(this.preferredMethod);
        this.isLoading = false;
        this.dispatchEvent(new CustomEvent('done', { bubbles: true, composed: true }));
        this.dialogOpened = false;
    }

    public cancel() {
        this.phoneNumber = undefined;
        this.errorMessage = undefined;
        this.dialogOpened = false;
        this.state = enterState;
    }

    public requestOtherContactMethod() {
        if (!this.verificationMethod) {
            return;
        }

        this.verificationMethod = this.verificationMethod.other;
        this.state = enterState;
    }

    public async sendOneTimePassword() {
        if (!this.phoneNumber || !this.verificationMethod) {
            throw new Error('Could not send one time password. Invalid configuration.');
        }

        this.isLoading = true;
        try {
            const response = await this.client?.sendOneTimePassword(
                this.phoneNumber.value,
                this.verificationMethod.id,
                this.useForBoth
            );

            if (response?.status === 'Success') {
                this.state = verifyState;
                this.errorMessage = undefined;
            } else {
                this.state = enterState;
                this.errorMessage = response?.message;
            }
        } catch (e) {
            this.state = enterState;
            this.errorMessage =
                e instanceof Error ? e.message : 'An error occurred. Please try again.';
        }
        this.isLoading = false;
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('isLoading')) {
            this.dispatchEvent(
                new CustomEvent('loadingStatusChange', {
                    bubbles: true,
                    composed: true,
                    detail: this.isLoading,
                })
            );
        }
    }

    protected render() {
        if (!this.verificationMethod) {
            return nothing;
        }
        return html`<vaadin-dialog
            id="verification-dialog"
            header-title="${this.title}"
            .opened=${this.dialogOpened}
            @opened-changed=${(event: DialogOpenedChangedEvent) => {
                if (!event.detail.value) {
                    this.dispatchEvent(
                        new CustomEvent('dialogClosed', { bubbles: true, composed: true })
                    );
                }
            }}
            ${dialogRenderer(this.renderDialogBody, [this.state, this.errorMessage])}
            ${dialogFooterRenderer(this.renderDialogFooter, [
                this.isOneTimePasswordValid,
                this.phoneNumber,
                this.isLoading,
                this.state,
            ])}
        ></vaadin-dialog>`;
    }

    private renderDialogBody() {
        return this.state.renderBody(this);
    }

    private renderDialogFooter() {
        return this.state.renderActions(this);
    }

    public static get styles() {
        return [
            css`
                .omega-dialog {
                    min-width: 520px;
                    border-radius: 0px;
                    padding: 0;
                }

                .dialog-header {
                    display: flex;
                    border-bottom: 1px solid #ccc;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 15px;
                }
                .dialog-header-title {
                    font-size: 22px;
                    font-weight: 600;
                    color: var(--omega-text-default);
                }

                .body {
                    padding: 15px;
                }

                footer {
                    width: 100%;
                    border-top: 1px solid #ccc;
                    padding: 8px;
                }
            `,
        ] as CSSResult[];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: OobRegistrationDialog;
    }
}
